// spreadsheet utils
import * as R from 'ramda'
import * as xlsx from 'xlsx'
// import { parseCandidateRow, isValidCandidate } from 'utils/parseCandidateData'

// Hard limits for spreadsheet imports
export const XLSX_MAX_FILES = 10
export const XLSX_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024 // 5 MB
export const XLSX_MAX_ROWS_PER_SHEET = 1000

// const xlsx = await import('xlsx')

// Worksheet -> [[*]]
const sheetToMatrix = (sheet: xlsx.WorkSheet): unknown[][] =>
  xlsx.utils.sheet_to_json(sheet, { header: 1 }) as unknown[][]

const correctHeader = ['name', 'email', 'phone']

const pedanticParseSheet = (sheet: xlsx.WorkSheet) => {
  // headers of spreadsheet must be exactly ['name', 'email', 'phone']
  // returns [{name, mail, phone}]
  // convert sheet object to array of arrays
  const [header = [], ...rows] = sheetToMatrix(sheet)
  const headerCells = Array.isArray(header) ? header : []

  if (rows.length > XLSX_MAX_ROWS_PER_SHEET)
    throw new Error(
      `too many rows in spreadsheet (${rows.length}), maximum is ${XLSX_MAX_ROWS_PER_SHEET}`,
    )

  if (R.equals(correctHeader, headerCells.slice(0, 3)))
    return R.pipe(
      R.map(([name, email, phone]) => ({ name, email, phone })),
      R.filter(R.prop('email')),
    )(rows)
  throw new Error(
    `wrong spreadsheet headers: ${headerCells}, expected ${correctHeader}`,
  )
}

const extension = R.pipe(
  R.split('.'),
  R.last,
)

export const processWorkbooks = R.pipe(
  R.chain(
    R.pipe(
      R.propOr({}, 'Sheets'),
      R.values,
    ),
  ),
  R.map(pedanticParseSheet),
  // R.map(sheetToMatrix),
  R.unnest,
  // R.map(parseCandidateRow),
  // R.filter(isValidCandidate),
)

// File -> Promise[xlsx.workbook]
const readSpreadsheetFile = (file: File): Promise<xlsx.WorkBook> =>
  new Promise((resolve, reject) => {
    if (typeof file.size === 'number' && file.size > XLSX_MAX_FILE_SIZE_BYTES) {
      reject(
        new Error(
          `file "${
            file.name
          }" is too large, maximum size is ${XLSX_MAX_FILE_SIZE_BYTES /
            (1024 * 1024)}MB`,
        ),
      )
      return
    }

    const reader = new FileReader()
    const decoder = new TextDecoder('utf-8')
    reader.onload = () => {
      try {
        const result = reader.result
        if (!(result instanceof ArrayBuffer)) {
          reject(new Error(`cannot read file "${file.name}"`))
          return
        }

        const byteData = new Uint8Array(result)
        const sheet =
          extension(file.name) === 'csv'
            ? xlsx.read(decoder.decode(byteData), { type: 'string' })
            : xlsx.read(byteData, { type: 'array' })
        resolve(sheet)
      } catch (error) {
        reject(error)
      }
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })

// FileList -> Promise[[{k:v}]]
export const importCandidateSheet = (files: FileList | File[]) => {
  const fileArray = Array.from(files)

  if (fileArray.length > XLSX_MAX_FILES)
    return Promise.reject(
      new Error(`too many files, maximum number of files is ${XLSX_MAX_FILES}`),
    )

  return Promise.all(fileArray.map(readSpreadsheetFile)).then(processWorkbooks)
}

const getMimeType = (filename: string) =>
  ({
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    csv: 'text/csv',
  }[extension(filename)])

// String -> Promise[File]
export const templateDocument = (filename = 'candidate.template.xlsx') => {
  const row = {}
  for (const key of correctHeader) row[key] = ''
  return exportCandidateSheet([row], filename)
}

const columnWidths = R.pipe(
  R.converge(R.prepend, [
    R.pipe(
      R.head,
      R.keys,
    ),
    R.map(R.values),
  ]),
  R.transpose,
  R.map(
    R.pipe(
      R.filter(R.is(String)),
      R.map(R.length),
      R.reduce(R.max, 0),
      R.add(2),
      R.objOf('wch'),
    ),
  ),
)

export const getFileName = (model: string): string => {
  const date = new Date().toISOString().slice(0, 10)

  return `ebber.${model}.${date}`
}

// [{k:v}] -> Promise[File]
export const exportCandidateSheet = (
  rows: Array<Record<string, unknown>>,
  fileName = 'data.xlsx',
): Promise<File> => {
  const worksheetName = 'data'
  const ext = extension(fileName)
  const worksheet = xlsx.utils.json_to_sheet(rows)
  // set column widths (32 chars)
  worksheet['!cols'] = columnWidths(rows)
  const workbook = xlsx.utils.book_new()
  xlsx.utils.book_append_sheet(workbook, worksheet, worksheetName)
  const writerOptions: xlsx.WritingOptions = {
    type: 'array',
    bookType: ext as xlsx.BookType,
  }
  const arrayBuffer = xlsx.write(workbook, writerOptions)
  return new Promise(resolve => {
    const file = new File([arrayBuffer], fileName, {
      type: getMimeType(fileName),
    })
    resolve(file)
  })
}
